import { isObject, isFunction, isArray, isUndefined, lowerCase } from "lodash";

// From:
// https://github.com/angular/angular.js/blob/master/src/ng/filter/filter.js

// Modified to remove unnecessary options while continuing reproduce existing
// filtering functionality in roster list, which filters text based on any
// property of the object, even those not visible in the table.

// The MIT License

// Copyright (c) 2010-2020 Google LLC. http://angularjs.org

// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:

// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.

// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

export const filterByTextOnObject = (searchText) => (item) =>
  deepCompare(item, searchText, compare, isObject(item));

function compare(actual, expected) {
  if (isUndefined(actual)) {
    // No substring matching against `undefined`
    return false;
  }
  if (actual === null || expected === null) {
    // No substring matching against `null`; only match against `null`
    return actual === expected;
  }
  if (isObject(expected) || isObject(actual)) {
    // Should not compare primitives against objects, unless they have custom `toString` method
    return false;
  }

  actual = lowerCase("" + actual);
  expected = lowerCase("" + expected);

  return actual.indexOf(expected) !== -1;
}

function deepCompare(
  actual,
  expected,
  comparator,
  matchAgainstAnyProp,
  dontMatchWholeObject
) {
  var actualType = getTypeForFilter(actual);
  var expectedType = getTypeForFilter(expected);

  if (expectedType === "string" && expected.charAt(0) === "!") {
    return !deepCompare(
      actual,
      expected.substring(1),
      comparator,
      matchAgainstAnyProp
    );
  } else if (isArray(actual)) {
    // In case `actual` is an array, consider it a match
    // if ANY of it's items matches `expected`
    return actual.some(function (item) {
      return deepCompare(item, expected, comparator, matchAgainstAnyProp);
    });
  }

  switch (actualType) {
    case "object":
      var key;
      if (matchAgainstAnyProp) {
        for (key in actual) {
          // Under certain, rare, circumstances, key may not be a string and `charAt` will be undefined
          // See: https://github.com/angular/angular.js/issues/15644
          if (
            key.charAt &&
            key.charAt(0) !== "$" &&
            deepCompare(actual[key], expected, comparator, true)
          ) {
            return true;
          }
        }
        return dontMatchWholeObject
          ? false
          : deepCompare(actual, expected, comparator, false);
      } else if (expectedType === "object") {
        for (key in expected) {
          var expectedVal = expected[key];
          if (isFunction(expectedVal) || isUndefined(expectedVal)) {
            continue;
          }

          var matchAnyProperty = false;
          var actualVal = actual;
          if (
            !deepCompare(
              actualVal,
              expectedVal,
              comparator,
              matchAnyProperty,
              matchAnyProperty
            )
          ) {
            return false;
          }
        }
        return true;
      } else {
        return comparator(actual, expected);
      }
    case "function":
      return false;
    default:
      return comparator(actual, expected);
  }
}

// Used for easily differentiating between `null` and actual `object`
function getTypeForFilter(val) {
  return val === null ? "null" : typeof val;
}
